import { ChangeDetectionStrategy, Component, effect, inject, linkedSignal, signal } from '@angular/core';
import { FormField, form, hidden, min, required, validate } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';

import moment from 'moment';
import { finalize } from 'rxjs';

import { Project, Purchased } from '../schemas';
import { AgreementService, Messenger, ProductService } from '../service';

export type HoursType = 'purchased' | 'profited' | 'uncharged' | 'unforeseen';
export interface ExtraHoursData {
  title?: string;
  item?: Purchased;
  type: 'purchased' | 'profited' | 'uncharged' | 'unforeseen';
  projects: Project[];
  service: AgreementService | ProductService;
  owner: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressBarModule,
    MatFormFieldModule,
    FormField,
    MatDatepickerModule,
    MatCheckboxModule,
    MatSelectModule,
  ],

  templateUrl: './extra-hours-form.html',
  styles: ``,
})
export class ExtraHoursForm {
  data = inject(MAT_DIALOG_DATA) as ExtraHoursData;
  messenger = inject(Messenger);
  hostDialog = inject(MatDialogRef);

  pending = signal(false);
  statuses = signal([
    [0, 'Not ready to invoice'],
    [10, 'Ready to invoice'],
    [20, 'Invoiced'],
  ]);

  months = signal(moment.months());
  yearRange = signal(Array.from({ length: 7 }).map((_, idx) => new Date().getFullYear() - 3 + idx));

  model = linkedSignal(() => {
    const now = new Date();
    const model = {
      Id: '',
      Month: this.months()[new Date().getMonth()],
      Year: now.getFullYear(),
      Hours: 0,
      Notes: '',
      ExpireMonth: '',
      ExpireYear: '',
      DueDate: '',
      Commissionable: false,
      HourlyRate: 175,
      Value: 0,
      Currency: 'AUD',
      Rate: 1,
      Status: 0,
      ProjectId: '',
      PurchaseOrder: '',
      ...this.data.item,
    };
    return model;
  });

  isCharged = linkedSignal(() => this.data.type === 'purchased');
  isUnforeseen = linkedSignal(() => this.data.type === 'unforeseen');

  projects = linkedSignal<Project[]>(() => this.data.projects).asReadonly();

  form = form(this.model, (schema) => {
    required(schema.Month);
    required(schema.Year);

    required(schema.Hours);
    if (this.isUnforeseen()) min(schema.Hours, 1);

    required(schema.Notes);

    // ExpireMonth and ExpireYear are interdependent - both required if either is set
    required(schema.ExpireMonth, { when: ({ valueOf }) => !!valueOf(schema.ExpireYear) });
    required(schema.ExpireYear, { when: ({ valueOf }) => !!valueOf(schema.ExpireMonth) });

    // DueDate is only required for purchased hours
    if (this.isCharged()) required(schema.DueDate);

    // HourlyRate required only for commissionable hours
    required(schema.HourlyRate, { when: ({ valueOf }) => valueOf(schema.Commissionable) });
    validate(schema.HourlyRate, ({ valueOf, value }) => {
      if (valueOf(schema.Commissionable) && value() < 1) {
        return { kind: 'min', message: 'Must be a positive number' };
      }
      return null;
    });

    if (!this.isUnforeseen()) required(schema.Value);

    required(schema.Currency, { when: ({ valueOf }) => valueOf(schema.Commissionable) });

    required(schema.Rate);
    // Hide Rate field for AUD currency (no conversion needed)
    hidden(schema.Rate, ({ valueOf }) => valueOf(schema.Currency) === 'AUD');

    min(schema.Rate, 0);
  });

  constructor() {
    effect(() => {
      const rate = this.form.HourlyRate().value();
      const hours = this.form.Hours().value();
      this.form.Value().value.set(rate * hours);
    });

    effect(() => {
      const project = this.form.ProjectId().value();
      if (!project) return;

      const existing = this.projects().find(({ Id }) => Id === project);
      if (existing) {
        this.form.PurchaseOrder().value.set(existing.PurchaseOrder);
      }
    });
  }

  save() {
    this.pending.set(true);
    const data = this.form().value();
    const req$ = data.Id
      ? this.data.service.updateHours(this.data.owner, data, this.data.type)
      : this.data.service.addHours(this.data.owner, data, this.data.type);

    req$.pipe(finalize(() => this.pending.set(false))).subscribe({
      next: () => this.hostDialog.close(true),
      error: this.messenger.handleHttpError,
    });
  }
}
